import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

const BG = "https://images.pexels.com/photos/32471032/pexels-photo-32471032.jpeg";

export default function Login() {
  const { setUser, loadTeam } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await loadTeam();
      navigate("/");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="stadium lights" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Logo size="lg" />
          <div>
            <h1 className="font-heading text-6xl xl:text-7xl font-black uppercase tracking-tighter leading-[0.9] text-white">
              Every play.<br />On the <span className="text-primary">sideline.</span><br />Instantly.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-md text-base">
              Record, tag, and push game film to your coaches the moment you hit stop. Built for the field.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-10"><Logo /></div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Sign in</p>
          <h2 className="font-heading text-4xl font-black uppercase tracking-tight mt-1 mb-8">Welcome back, Coach</h2>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@team.com"
                className="h-12 bg-card border-border text-base" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-secondary hover:underline">Forgot password?</Link>
              </div>
              <Input id="password" data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="h-12 bg-card border-border text-base" />
            </div>

            {error && <p data-testid="login-error" className="text-sm font-semibold text-primary">{error}</p>}

            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full h-12 bg-primary hover:bg-red-500 text-white font-heading font-bold uppercase tracking-widest text-base transition-colors">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enter Coach View"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            New to the team?{" "}
            <Link to="/register" data-testid="go-to-register-link" className="font-bold text-white hover:text-primary transition-colors">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
